import React from 'react'
import dayjs from 'dayjs'
import type { PushApiError } from '../../apiService/Core/PushApiCall'
import type { AudiencePayload, AudienceState, CampaignStatus } from './types'

export const ACTIVE_CAMPAIGN_STATUSES: CampaignStatus[] = ['FREEZING', 'QUEUED', 'RUNNING']
export const CAMPAIGN_STATUSES: CampaignStatus[] = [
    'FREEZING', 'QUEUED', 'RUNNING', 'COMPLETED', 'COMPLETED_WITH_FAILURES', 'FAILED', 'CANCELLED',
]
export const POLL_INTERVAL_MS = 4000
export const LIST_POLL_INTERVAL_MS = 8000
export const TITLE_MAX = 65
export const BODY_MAX = 240
export const MANUAL_USERS_MAX = 5000
export const TEST_USERS_MAX = 10
export const GROUP_MEMBERS_PER_REQUEST = 1000

export const SEGMENT_OPTIONS: { key: string; label: string; help: string }[] = [
    { key: 'WOLFPACK', label: 'Wolfpack users', help: 'Users of the Wolfpack app' },
    { key: 'BOOSTER_LOAN', label: 'BoosterLoan users', help: 'Users registered in BoosterLoan' },
    { key: 'ONBOARDED_NOT_BOOSTER', label: 'Onboarded but not in BoosterLoan', help: 'Completed onboarding, not registered in BoosterLoan' },
    { key: 'BOOSTER_ELIGIBLE', label: 'Onboarded and available for BoosterLoans', help: 'Completed onboarding and eligible to take BoosterLoans' },
    { key: 'FUNDED', label: 'Funded users', help: 'At least one completed deposit (wire or bank transfer)' },
    { key: 'NON_FUNDED', label: 'Non-funded users', help: 'No completed deposit yet' },
    { key: 'NOT_ONBOARDED', label: 'Account created, onboarding not started', help: 'Signed up but has not started onboarding or identity verification' },
    { key: 'ONBOARDING_INCOMPLETE', label: 'Onboarding started, not completed', help: 'Started onboarding or verification but did not finish' },
]

export const fmtDate = (value?: string | null) => {
    if (!value) return '-'
    const d = dayjs(value)
    return d.isValid() ? d.format('DD/MM/YYYY HH:mm') : '-'
}

export const fmtNum = (n?: number | null) => (typeof n === 'number' ? n.toLocaleString() : '0')

export const regionName = (code: string) => {
    try {
        const dn = new (Intl as any).DisplayNames(['en'], { type: 'region' })
        return dn.of(code) || code
    } catch (e) {
        return code
    }
}

/** Query-string builder that skips empty values. */
export const qs = (params: Record<string, any>) => {
    const sp = new URLSearchParams()
    Object.entries(params).forEach(([k, v]) => {
        if (v !== undefined && v !== null && v !== '') sp.append(k, String(v))
    })
    const s = sp.toString()
    return s ? `?${s}` : ''
}

/** baseApiCall throws strings/arrays, push calls throw PushApiError; normalise both. Never includes tokens. */
export const getErrorMessage = (error: any): string => {
    if (!error) return 'Something went wrong'
    if (typeof error === 'string') return error
    if (Array.isArray(error)) return error.map(String).join(', ')
    if (Array.isArray(error.errors) && error.errors.length) {
        return error.errors.map((e: any) => (e?.field ? `${e.field}: ${e.message}` : e?.message)).join(', ')
    }
    if (typeof error.message === 'string' && error.message) return error.message
    return 'Something went wrong'
}

export const getErrorStatus = (error: any): number | undefined =>
    error && typeof error === 'object' ? (error as PushApiError).status : undefined

/** Backend kill switch: ENABLE_PUSH_CAMPAIGNS is off. */
export const isPushDisabled = (error: any): boolean =>
    getErrorStatus(error) === 503 && /not enabled/i.test(getErrorMessage(error))

export const emptyAudience = (): AudienceState => ({
    includeAll: false, segments: [], groupIds: [], countries: [], states: [], users: [],
})

export const toAudiencePayload = (a: AudienceState): AudiencePayload => ({
    includeAll: a.includeAll,
    segments: a.segments,
    groupIds: a.groupIds,
    countries: a.countries,
    states: a.states,
    userIds: a.users.map((u) => u.id),
})

export const isAudienceEmpty = (a: AudienceState) =>
    !a.includeAll && a.segments.length === 0 && a.groupIds.length === 0 &&
    a.countries.length === 0 && a.states.length === 0 && a.users.length === 0

export const newIdempotencyKey = () => {
    try {
        const c: any = typeof globalThis !== 'undefined' ? (globalThis as any).crypto : undefined
        if (c && typeof c.randomUUID === 'function') return c.randomUUID()
        if (c && typeof c.getRandomValues === 'function') {
            const b = new Uint8Array(16)
            c.getRandomValues(b)
            b[6] = (b[6] & 0x0f) | 0x40
            b[8] = (b[8] & 0x3f) | 0x80
            const h = Array.from(b, (x) => x.toString(16).padStart(2, '0')).join('')
            return `${h.slice(0, 8)}-${h.slice(8, 12)}-${h.slice(12, 16)}-${h.slice(16, 20)}-${h.slice(20)}`
        }
    } catch (e) {
        // fall through
    }
    return `${Date.now().toString(16)}-${Math.random().toString(16).slice(2)}-${Math.random().toString(16).slice(2)}`
}

/** Debounce a changing value (500ms like AuditLogs by default). */
export const useDebouncedValue = <T,>(value: T, delay = 500): T => {
    const [debounced, setDebounced] = React.useState<T>(value)
    React.useEffect(() => {
        const t = setTimeout(() => setDebounced(value), delay)
        return () => clearTimeout(t)
    }, [value, delay])
    return debounced
}

export const chunk = <T,>(arr: T[], size: number): T[][] => {
    const out: T[][] = []
    for (let i = 0; i < arr.length; i += size) out.push(arr.slice(i, i + size))
    return out
}
